<template>
  <div data-cy="tablePaginator" class="sui-pagination">
    <!-- Селект с выбором размера страницы -->
    <div
      v-on-click-outside="closeMenu"
      class="sui-pagination__select"
      :class="{ 'sui-pagination__select--open': isMenuOpen }"
    >
      <button
        type="button"
        data-cy="countSelect"
        class="font-body-s"
        @click="toggleMenu()"
      >
        {{ pageSize }}
        <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.71271 4.86014L0.792231 0.941034C0.633384 0.784669 0.633384 0.531823 0.792231 0.375458L1.03219 0.139247C1.19104 -0.0171186 1.4479 -0.0171186 1.60675 0.139247L4.99999 3.54268L8.39324 0.142574C8.55208 -0.0137915 8.80894 -0.0137915 8.96779 0.142574L9.20775 0.378785C9.3666 0.53515 9.3666 0.787996 9.20775 0.944361L5.28727 4.86347C5.12842 5.01651 4.87156 5.01651 4.71271 4.86014Z"/>
        </svg>
      </button>
      <div
        v-show="isMenuOpen"
        class="sui-pagination__menu"
      >
        <div
          v-for="pageSizeItem in pageSizeList"
          :key="pageSizeItem"
          data-cy="countList"
          class="sui-pagination__menu-item font-body-s"
          @click="selectPageSize(pageSizeItem)"
        >
          {{ pageSizeItem }}
        </div>
      </div>
    </div>
    <!-- Множество страниц -->
    <div class="sui-pagination__page-list">
      <div
        data-cy="previousPage"
        class="sui-pagination__arrow"
        :class="{
          'sui-pagination__arrow--disabled': currentPage == 1,
        }"
        @click="selectPrevPage"
      >
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.435506 5.07634L4.47338 1.03705C4.63448 0.873391 4.89499 0.873391 5.05609 1.03705L5.29946 1.28428C5.46056 1.44794 5.46056 1.71259 5.29946 1.87625L1.79289 5.37232L5.29603 8.86839C5.45714 9.03205 5.45714 9.29669 5.29603 9.46035L5.05266 9.70759C4.89156 9.87125 4.63105 9.87125 4.46995 9.70759L0.432078 5.6683C0.274402 5.50464 0.274403 5.24 0.435506 5.07634Z" fill="currentColor"/>
        </svg>
      </div>
      <template v-for="page in pageList" :key="page">
        <div
          data-cy="pageList"
          class="sui-pagination__page font-body-s"
          :class="{
            'sui-pagination__page--active': currentPage === page,
          }"
          @click="selectPage(page)"
        >
          {{ page }}
        </div>
        <div v-if="isPageFollowedByDots(page)" class="sui-pagination__dots font-body-s">...</div>
      </template>
      <div
        data-cy="nextPage"
        class="sui-pagination__arrow"
        :class="{
          'sui-pagination__arrow--disabled': currentPage >= props.totalPages,
        }"
        @click="selectNextPage"
      >
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.56449 5.07634L1.52662 1.03705C1.36552 0.873391 1.10501 0.873391 0.943909 1.03705L0.70054 1.28428C0.539436 1.44794 0.539436 1.71259 0.70054 1.87625L4.20711 5.37232L0.703967 8.86839C0.542864 9.03205 0.542864 9.29669 0.703967 9.46035L0.947336 9.70759C1.10844 9.87125 1.36895 9.87125 1.53005 9.70759L5.56792 5.6683C5.7256 5.50464 5.7256 5.24 5.56449 5.07634Z" fill="currentColor"/>
        </svg>
      </div>
    </div>
    <!-- Инпут с выбором произвольной страницы -->
    <div class="sui-pagination__input">
      <input
        :value="inputValue"
        data-cy="pageInputValue"
        type="text"
        class="font-body-s"
        @input="onInputChange"
      />
      <button
        type="button"
        data-cy="goToButton"
        class="font-body-s-semibold"
        @click="submitInput"
      >
        {{ inputLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PropType } from 'vue'
import { vOnClickOutside } from '@vueuse/components'

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1
  },
  totalPages: {
    type: Number,
    default: 2
  },
  pageSize: {
    type: Number,
    default: 25
  },
  pageSizeList: {
    type: Array as PropType<number[]>,
    default: () => [25, 50, 100],
  },
  inputLabel: {
    type: String,
    default: 'Перейти',
  },
})

const emit = defineEmits<{
  'update:currentPage': [page: number];
  'update:pageSize': [pageSize: number];
}>()

const inputValue = ref(1)
const isMenuOpen = ref(false)

const pageList = computed(() => {
  const pages = [1]

  // если страница 1-5, то показываем 7, а если больше - то 5
  if (props.totalPages < 8) {
    for (let i = 2; i < props.totalPages; i++) {
      pages.push(i)
    }
  } else if (props.currentPage < 6) {
    pages.push(2, 3, 4, 5, 6, 7)
  } else if (props.currentPage > props.totalPages - 5) {
    pages.push(
      props.totalPages - 6,
      props.totalPages - 5,
      props.totalPages - 4,
      props.totalPages - 3,
      props.totalPages - 2,
      props.totalPages - 1
    )
  } else {
    pages.push(
      props.currentPage - 2,
      props.currentPage - 1,
      props.currentPage,
      props.currentPage + 1,
      props.currentPage + 2
    )
  }

  if (!pages.includes(props.totalPages)) {
    pages.push(props.totalPages)
  }

  return pages
})

const isPageFollowedByDots = (page: number) => (
  (page === 1 && !pageList.value.includes(2)) || (page === pageList.value[pageList.value.length - 2] && page + 1 !== props.totalPages)
)

const closeMenu = () => {
  isMenuOpen.value = false
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const selectNextPage = () => {
  if (props.totalPages > props.currentPage) {
    emit('update:currentPage', props.currentPage + 1)
  }
}

const selectPrevPage = () => {
  if (props.currentPage > 1) {
    emit('update:currentPage', props.currentPage - 1)
  }
}

const selectPage = (page: number) => {
  emit('update:currentPage', page)
}

const selectPageSize = (pageSize: number) => {
  emit('update:pageSize', pageSize)
  closeMenu()
}

const onInputChange = (event: Event) => {
  // NOTE: Почему-то приходится обновлять инпут руками
  const target = event.target as HTMLInputElement
  target.value = target.value.replace(/[^\d.-]+/g, '')
  inputValue.value = Number(target.value)
  if (inputValue.value > props.totalPages) {
    inputValue.value = props.totalPages
    target.value = String(props.totalPages)
  } else if (inputValue.value < 1) {
    inputValue.value = 1
    target.value = '1'
  }
}

const submitInput = () => {
  emit('update:currentPage', Number(inputValue.value))
}
</script>

<style lang="scss" scoped>
.sui-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &__page-list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
  }

  &__page {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 34px;
    height: 34px;
    padding: 0 4px;
    box-sizing: border-box;
    color: var(--color-text-primary);
    border-radius: var(--border-radius);
    cursor: pointer;
    text-decoration: none;
    user-select: none;

    &:hover:not(.sui-pagination__page--active, .sui-pagination__page--disabled) {
      color: var(--color-text-contrast);
      background-color: var(--color-icon-tertiary);
    }

    &--active {
      color: var(--color-text-contrast);
      background-color: var(--color-hint);
    }

    &--disabled {
      color: var(--color-text-tertiary);
      pointer-events: none;
    }
  }

  &__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    color: var(--color-icon-primary);
    user-select: none;
  }

  &__arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    color: var(--color-icon-primary);
    border-radius: var(--border-radius);
    cursor: pointer;

    svg {
      display: block;
    }

    &:hover:not(.sui-pagination__arrow--disabled) {
      color: var(--color-text-contrast);
      background-color: var(--color-icon-tertiary);
    }

    &--disabled {
      color: var(--color-icon-tertiary);
      pointer-events: none;
    }
  }

  &__input {
    display: flex;
    align-items: center;
    gap: 4px;

    input {
      width: 54px;
      height: 44px;
      padding: 0;
      text-align: center;
      color: var(--color-text-primary);
      background-color: var(--color-button-contrast);
      border: none;
      outline: none;
      border-radius: var(--border-radius);
    }

    button {
      width: 116px;
      height: 44px;
      color: var(--color-text-secondary);
      border: none;
      background-color: transparent;
      text-decoration: none;
      cursor: pointer;
      transition: opacity 0.15s;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  &__select {
    position: relative;

    button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 70px;
      height: 44px;
      gap: 10px;
      color:var(--color-text-primary);
      background-color: var(--color-button-contrast);
      border-radius: var(--border-radius);
      border: none;
      cursor: pointer;
    }

    svg {
      display: block;
      fill: var(--color-icon-tertiary);
      transition: transform 0.2s ease-in-out;
    }

    &--open {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  &__menu {
    position: absolute;
    bottom: 100%;
    left: 0;
    z-index: 200;
    width: 70px;
    box-shadow: 0px 2px 6px 0px #7474741A;
    border: none;
    border-radius: var(--border-radius);
    background-color: var(--color-bg-primary);
    overflow: hidden; // скругляем углы при ховере
  }

  &__menu-item {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 70px;
    height: 44px;
    color: var(--color-text-primary);
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
      color: var(--color-text-contrast);
      background-color: var(--color-icon-tertiary);
    }
  }
}
</style>
